import React from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Seo, { useLocalBusinessLd, SITE_URL } from "@/components/Seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SEO_PAGES } from "@/data/seoPages";
import TrustPartner from "@/components/TrustPartner";
import StatsBand from "@/components/StatsBand";
import Breadcrumbs from "@/components/Breadcrumbs";
import CommissionCalculator from "@/components/CommissionCalculator";
import { useContent } from "@/hooks/useContent";
import { TRUST_DEFAULTS } from "@/lib/contentDefaults";
import { Check, ArrowRight, Phone, MapPin, Star, Building2 } from "lucide-react";

export default function SeoLanding() {
  const businessLd = useLocalBusinessLd();
  const trust = useContent("trust_badges", TRUST_DEFAULTS);
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").replace(/\/$/, "");
  const page = SEO_PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: (page.faq || []).map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const owner = page.audience !== "traveler";
  const crumbCat = owner
    ? { label: "Conciergerie à Rouen", to: "/conciergerie-airbnb-rouen" }
    : { label: "Consigne à bagages", to: "/consigne" };
  const crumbs = [{ label: "Accueil", to: "/" }];
  if (`/${slug}` !== crumbCat.to) crumbs.push(crumbCat);
  crumbs.push({ label: page.breadcrumb || page.h1 });
  const primaryCta = page.ctaTo
    ? { to: page.ctaTo, label: page.ctaLabel || "En savoir plus" }
    : owner
    ? { to: "/estimation-revenus-airbnb-rouen", label: "Estimer mes revenus" }
    : { to: "/consigne", label: "Réserver ma consigne" };
  const secondaryCta = owner
    ? { to: "/confier-mon-logement", label: "Confier mon logement" }
    : { to: "/logements", label: "Voir les logements" };
  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service",
    serviceType: page.h1, provider: { "@type": "LocalBusiness", name: "NJ Conciergerie" },
    areaServed: "Rouen", url: `${SITE_URL}/${slug}`,
  };
  const articleLd = page.datePublished ? {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: page.h1, description: page.metaDescription, image: page.heroImage,
    datePublished: page.datePublished, dateModified: page.dateModified || page.datePublished,
    author: { "@type": "Organization", name: page.author || "NJ Conciergerie" },
    publisher: { "@type": "Organization", name: "NJ Conciergerie" },
    mainEntityOfPage: `${SITE_URL}/${slug}`,
  } : null;

  return (
    <Layout>
      <Seo title={page.metaTitle} description={page.metaDescription} canonical={`/${slug}`}
        image={page.heroImage} jsonLd={[businessLd, serviceLd, faqLd, ...(articleLd ? [articleLd] : [])]} />

      {/* HERO */}
      <section className="relative h-[52vh] min-h-[380px] flex items-center" data-testid="seo-landing">
        <img src={page.heroImage} alt={page.h1} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-nj-dark/60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <span className="text-nj-gold text-xs tracking-[0.28em] uppercase font-semibold">{page.kicker}</span>
          <h1 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl leading-[1.08]">{page.h1}</h1>
          <p className="text-white/85 text-lg mt-5 max-w-2xl leading-relaxed">{page.intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={primaryCta.to} data-testid="seo-cta-primary" className="bg-nj-gold text-nj-dark font-semibold px-7 py-3.5 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2">
              {primaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:+33666934267" className="border border-white/60 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> 06 66 93 42 67
            </a>
          </div>
        </div>
      </section>

      <Breadcrumbs items={crumbs} />

      {/* PROOF BAND */}
      <section className="bg-nj-primary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-wrap justify-center gap-x-12 gap-y-6 text-center">
          <div className="min-w-[130px]"><div className="font-serif text-3xl text-nj-gold">Rouen</div><div className="text-white/70 text-sm mt-1">Équipe 100% locale</div></div>
          {trust.show_google_rating && (
            <div className="min-w-[130px]"><div className="font-serif text-3xl text-nj-gold flex items-center justify-center gap-1">{trust.google_rating} <Star className="w-5 h-5 fill-nj-gold" /></div><div className="text-white/70 text-sm mt-1">{trust.google_rating_label}</div></div>
          )}
          <div className="min-w-[130px]"><div className="font-serif text-3xl text-nj-gold">Airbnb · Booking</div><div className="text-white/70 text-sm mt-1">Diffusion multi-plateformes</div></div>
          <div className="min-w-[130px]"><div className="font-serif text-3xl text-nj-gold flex items-center justify-center gap-1"><Building2 className="w-6 h-6" /> Agence</div><div className="text-white/70 text-sm mt-1">Bd de la Marne, Rouen</div></div>
        </div>
      </section>

      <TrustPartner />

      {owner && <StatsBand />}

      {/* SECTIONS */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {page.sections.map((s) => (
          <div key={s.h2} className="mb-10">
            <h2 className="font-serif font-light text-3xl text-nj-text mb-4">{s.h2}</h2>
            {s.paragraphs.map((p, i) => <p key={`${s.h2}-${i}`} className="text-nj-text2 leading-relaxed mb-3">{p}</p>)}
          </div>
        ))}
      </section>

      {page.calculator && <CommissionCalculator rate={page.commissionRate || 20} />}

      {/* SOURCES / DISCLAIMER (guides) */}
      {(page.sources || page.disclaimer) && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="seo-sources">
          {page.dateModified && <p className="text-xs text-nj-muted mb-3">Mis à jour le {page.dateModified}</p>}
          {page.sources && (
            <div className="bg-nj-olive/40 rounded-md p-5">
              <h2 className="font-serif text-xl text-nj-text mb-2">Sources officielles</h2>
              <ul className="text-sm text-nj-text2 space-y-1 list-disc pl-5">
                {page.sources.map((s) => (
                  <li key={s.url}><a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-nj-primary underline">{s.label}</a></li>
                ))}
              </ul>
            </div>
          )}
          {page.disclaimer && <p className="text-xs text-nj-muted mt-4 italic">{page.disclaimer}</p>}
        </section>
      )}

      {/* RELATED (pillar cluster links) */}
      {page.related && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="seo-related">
          <h2 className="font-serif text-2xl text-nj-text mb-6">À explorer aussi</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {page.related.map((r) => (
              <a key={r.to} href={r.to} className="block bg-white border border-nj-olive rounded-md p-5 hover:border-nj-primary transition-colors">
                <span className="font-medium text-nj-text">{r.label}</span>
                {r.text && <p className="text-sm text-nj-muted mt-1">{r.text}</p>}
              </a>
            ))}
          </div>
        </section>
      )}

      {/* BULLETS */}
      {page.bullets && (
        <section className="bg-nj-olive/40 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {page.bullets.map((b) => (
              <div key={b.title} className="bg-white border border-nj-olive rounded-md p-6">
                <Check className="w-5 h-5 text-nj-primary mb-3" />
                <h3 className="font-serif text-lg text-nj-text">{b.title}</h3>
                <p className="text-sm text-nj-text2 mt-1.5 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {page.faq && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-serif font-light text-3xl text-nj-text mb-6">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="w-full">
            {page.faq.map((f, i) => (
              <AccordionItem key={f.q} value={`f${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-medium text-nj-text">{f.q}</AccordionTrigger>
                <AccordionContent data-testid={`faq-content-${i}`} className="text-nj-text2 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* CTA */}
      <section className="bg-nj-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="font-serif font-light text-3xl sm:text-4xl">{page.ctaTitle}</h2>
          <p className="text-white/75 mt-3">{page.ctaText}</p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Link to={primaryCta.to} className="bg-nj-gold text-nj-dark font-semibold px-7 py-3.5 rounded-sm hover:brightness-105 transition-all inline-flex items-center gap-2">
              {primaryCta.label} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={secondaryCta.to} className="border border-white/60 text-white font-semibold px-7 py-3.5 rounded-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <MapPin className="w-4 h-4" /> {secondaryCta.label}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
